<template>
  <div id="app" class="bg-paper">
    <!-- Sidebar (only show when authenticated) -->
    <Sidebar
      v-if="isAuthenticated"
      :collapsed="sidebarCollapsed"
      :userData="userData"
      :regions="areas"
      :currentRegion="selectedArea"
      @toggle="sidebarCollapsed = !sidebarCollapsed"
      @logout="logout"
      @change-region="changeArea"
    />

    <!-- Main Content -->
    <main
      :class="[
        'min-h-screen transition-all duration-300',
        isAuthenticated ? (sidebarCollapsed ? 'ml-20' : 'ml-72') : ''
      ]"
    >
      <router-view :key="mainKey"></router-view>
    </main>

    <Transition name="fade">
      <LoadingOverlay :visible="loading" />
    </Transition>

    <!-- Global Toast Notifications -->
    <ToastNotification />
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { useRouter, useRoute } from "vue-router";
import Sidebar from "./components/Navigation/Sidebar.vue";
import ToastNotification from "./components/UI/ToastNotification.vue";
import LoadingOverlay from "./components/UI/LoadingOverlay.vue";
import { useNotification } from "@/composables/useNotification";
import { useAppStore } from "@/stores/app";
import { useMapStore } from "@/stores/map";
import { useUserStore } from "@/stores/user";
import { useAuthStore } from "@/stores/auth";
import { useAreasStore } from "@/stores/areas";

export default {
  name: "App",
  components: {
    Sidebar,
    ToastNotification,
    LoadingOverlay
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const appStore = useAppStore();
    const mapStore = useMapStore();
    const userStore = useUserStore();
    const authStore = useAuthStore();
    const areasStore = useAreasStore();
    const notify = useNotification();

    // State
    const mainKey = ref(0);
    const selectedArea = ref("");
    const sidebarCollapsed = ref(false);

    // Computed
    const loading = computed(() => appStore.loading);
    const userData = computed(() => userStore.userData);
    const isAuthenticated = computed(() => {
      return route.name !== "Home" && route.name !== "Encuesta";
    });

    // Methods
    const handleResize = () => {
      appStore.updateWindow(window.innerWidth, window.innerHeight);
      if (window.innerWidth < 1024) {
        sidebarCollapsed.value = true;
      }
    };

    // Computed areas list from store
    const areas = computed(() => areasStore.list);

    const fetchMapData = async (areaId) => {
      try {
        const areaData = await areasStore.getAreaData(areaId);
        if (areaData) {
          // Set area ID first (needed for convention-based paths)
          mapStore.setArea(areaId);
          // Then update map metadata (center, zoom)
          mapStore.updateMapData(areaData);
          return true;
        } else {
          notify.error("Error al obtener datos, comunicarse con el administrador");
          return false;
        }
      } catch (error) {
        notify.error("Error al cargar datos del área");
        return false;
      }
    };

    const getElectionsData = async () => {
      if (!mapStore.hasElectoralData || !mapStore.hasSectionData) {
        appStore.toggleLoading();
        await mapStore.loadElectoralData();
        appStore.toggleLoading();
      }
    };

    const getAGEBData = async () => {
      if (!mapStore.hasAgebShapefile || !mapStore.hasAgebData) {
        appStore.toggleLoading();
        await mapStore.loadAGEBData();
        appStore.toggleLoading();
      }
    };

    const getIndexData = async () => {
      if (!mapStore.hasAgebShapefile || !mapStore.hasIndexData) {
        appStore.toggleLoading();
        await mapStore.loadIndexData();
        appStore.toggleLoading();
      }
    };

    const handleRouteChange = async (routeName) => {
      // Skip if no area is set yet
      if (!mapStore.area) {
        return;
      }

      switch (routeName) {
        case "Electoral":
          await getElectionsData();
          break;
        case "AGEBs":
          await getAGEBData();
          break;
        case "Indices":
          await getIndexData();
          break;
      }
      mainKey.value++;
    };

    const loadDataForCurrentRoute = async () => {
      const routeName = route.name;
      if (routeName === "AGEBs") {
        await mapStore.loadAGEBData();
      } else if (routeName === "Electoral") {
        await mapStore.loadElectoralData();
      } else if (routeName === "Indices") {
        await mapStore.loadIndexData();
      } else {
        await mapStore.loadAllData();
      }
    };

    const changeArea = async (area) => {
      if (selectedArea.value === area) {
        notify.warning("Ya estas en " + area.toUpperCase());
        return;
      }
      selectedArea.value = area;
      areasStore.setCurrentArea(area);
      mapStore.clearMapData();
      appStore.toggleLoading();

      await fetchMapData(area);
      await loadDataForCurrentRoute();

      appStore.toggleLoading();
      mainKey.value++;
    };

    const logout = async () => {
      try {
        await authStore.signOutUser();
        mapStore.clearMapData();
        router.push("/");
      } catch (error) {
        notify.error(error.message);
      }
    };

    // Watch route changes
    watch(
      () => route.name,
      (newName) => {
        handleRouteChange(newName);
      }
    );

    // Initialize default area
    const initDefaultArea = async () => {
      await areasStore.fetchAreas();
      if (areasStore.list.length > 0 && !selectedArea.value) {
        const defaultArea = areasStore.list[0];
        selectedArea.value = defaultArea;
        areasStore.setCurrentArea(defaultArea);
        await fetchMapData(defaultArea);
        // Load data for current route after area is set
        await handleRouteChange(route.name);
      }
    };

    // Lifecycle
    onMounted(() => {
      initDefaultArea();
      window.addEventListener("resize", handleResize);
      handleResize();
    });

    onBeforeUnmount(() => {
      window.removeEventListener("resize", handleResize);
    });

    return {
      areas,
      mainKey,
      selectedArea,
      sidebarCollapsed,
      loading,
      userData,
      isAuthenticated,
      changeArea,
      logout
    };
  }
};
</script>

<style>
/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Ensure proper z-index stacking */
.leaflet-pane {
  z-index: 1 !important;
}

.leaflet-control {
  z-index: 10 !important;
}
</style>
